import { useContext } from 'react'
import styled, { keyframes } from 'styled-components'
import Collapse from '../../../collapse'
import CaptionInput from '../../../caption-input'
import { CreationSourceEditorContext } from '../../manager'
import { SourceEditorCaption, SourceEditorCaptionStyle } from '../../types'
import { getDefaultCaptionPosition } from '../../utils'
import CaptionStyleOptions from './../style-options'
import LayerOptionsMenu from 'site/creation-tools/components/layer-options-menu'

const fadeIn = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
`

const Input = styled(CaptionInput)`
    animation: ${fadeIn} 0.2s ease-out;
`

type Props = {
    caption: SourceEditorCaption
    index: number
    onRemove: () => void
}

const CreationSourceEditorCaptionInput = ({ caption, index, onRemove }: Props) => {
    const {
        captions,
        setCaptions,
        source,
        edits,
        duplicate,
        flip,
        sendToBack,
        customFontUrl,
        toggleCaptionStyleOptions,
    } = useContext(CreationSourceEditorContext)

    const startTime = caption.start || 0
    const endTime = caption.end || source.duration
    const isLastIndex = captions.length - 1 === index && captions.length > 1

    const changeCaptionStyle = (captionStyle: SourceEditorCaptionStyle) => {
        const filledCaptions = captions.filter((c) => c.text.length > 0)
        const amITheLastFilled = filledCaptions[filledCaptions.length - 1]?.id === caption.id

        // Update caption styles
        setCaptions(
            captions.map((c, i) => {
                // Update the current caption
                if (i === index) {
                    return { ...c, style: captionStyle }
                }

                // Update the next caption with the current caption style
                if (amITheLastFilled && i === captions.length - 1) {
                    return { ...c, style: captionStyle }
                }

                return c
            })
        )
    }

    const onInput = (value: string) => {
        setCaptions(
            captions.map((c, i) => {
                if (i === index) {
                    return { ...c, text: value, ...(value ? {} : getDefaultCaptionPosition(source, edits)) }
                }
                return c
            })
        )
    }

    const onFocus = () => {
        if (!caption.options.isStyleOptionsOpened && isLastIndex) {
            toggleCaptionStyleOptions(caption)
        }
    }

    const onTimeUpdate = (start: number, end: number) => {
        setCaptions(
            captions.map((c, i) => {
                if (i === index) {
                    return { ...c, start: start, end: end }
                }
                return c
            })
        )
    }

    return (
        <Collapse
            header={
                <Input
                    {...{
                        value: caption.text,
                        onFocus,
                        onInput,
                        onRemove,
                        placeholder: isLastIndex ? 'Add another caption' : 'Type caption here',
                    }}
                />
            }
            isOpened={!!caption.options.isStyleOptionsOpened}
            onChange={() => toggleCaptionStyleOptions(caption)}
        >
            <CaptionStyleOptions
                captionStyle={caption.style}
                setCaptionStyle={changeCaptionStyle}
                customFontUrl={customFontUrl}
            />
            <LayerOptionsMenu
                source={source}
                timerOptions={{
                    startTime,
                    endTime,
                    onTimeUpdate,
                }}
                duplicate={() => duplicate(caption)}
                flip={() => flip(caption)}
                sendToBack={() => sendToBack(caption)}
            />
        </Collapse>
    )
}

export default CreationSourceEditorCaptionInput
